import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AddActionButton from "@/components/ui/add-action-button";
import SearchInput from "@/components/ui/search-input";
import Text from "@/components/ui/text";
import { ProductsTopBar } from "./components/ProductsTopBar";
import { ProductListCard } from "./components/ProductListCard";
import { useProductEditor } from "./hooks/useProductEditor";
import { useProductList } from "./hooks/useProductList";
import { PRODUCTS_SETTINGS_PATH } from "./consts";
import { ProductV2 } from "./types";

const TRANSLATION_KEY = "settings.product";

export const ProductListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { products, isLoading, refetch } = useProductList();
  const { openAddProduct, dialogs } = useProductEditor({
    products,
    refetchProducts: refetch
  });

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = products.filter(({ name }) =>
    name.toLowerCase().includes(normalizedSearch)
  );

  const openProduct = (product: ProductV2) =>
    navigate(`${PRODUCTS_SETTINGS_PATH}/${product.id}`);

  const renderContent = () => {
    if (isLoading) return null;

    if (filtered.length === 0 && normalizedSearch) {
      return (
        <Text
          variant="bodySecondary"
          className="w-full text-center"
          data-test="products-list-no-results"
        >
          {t(`${TRANSLATION_KEY}.noSearchResults`)}
        </Text>
      );
    }

    return (
      <div className="flex w-full flex-col gap-4" data-test="products-list">
        {filtered.map((product) => (
          <ProductListCard
            key={product.id}
            product={product}
            // Keeps each product's logo colour the same as on its own page.
            index={products.indexOf(product)}
            onOpen={openProduct}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex w-full flex-col" data-test="products-list-page">
      <ProductsTopBar />
      <div className="mb-4 flex w-full items-center justify-center">
        <div className="flex w-[800px] flex-col items-start gap-12 pt-12">
          <div className="flex w-full flex-col gap-6">
            <div className="flex flex-col">
              <Text variant="title3H1">{t(`${TRANSLATION_KEY}.title`)}</Text>
              <Text variant="bodySecondary">
                {t(`${TRANSLATION_KEY}.subtitle`)}
              </Text>
            </div>
            <div className="flex w-full items-center gap-2">
              <SearchInput
                className="flex-1"
                placeholder={t(`${TRANSLATION_KEY}.searchPlaceholder`)}
                value={search}
                onChange={setSearch}
              />
              <AddActionButton
                data-test="products-list-add-button"
                label={t(`${TRANSLATION_KEY}.addProduct`)}
                onClick={openAddProduct}
              />
            </div>
          </div>
          {renderContent()}
        </div>
      </div>
      {dialogs}
    </div>
  );
};
